import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Alert, Field, Muted, Stack, Strong, Textarea } from '../../components/ui';
import { useCreateInquiryChatMutation } from '../../services/stayService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

/**
 * Ask a worker's Provider a question before booking (spec 019 W12, T058). Same
 * inquiry chat as a place to stay, with the worker as the listing and no dates
 * or guests. If the customer books the worker, the chat continues in the job.
 */
export function ContactWorkerModal({
  workerCarId,
  workerName,
  onClose,
}: {
  workerCarId: string;
  workerName: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [create, { loading }] = useCreateInquiryChatMutation();

  const send = async () => {
    setError(null);
    try {
      const { data } = await create({
        variables: { input: { listingId: workerCarId, message: message.trim() } },
      });
      const chatId = data?.createInquiryChat.chatId;
      if (!chatId) throw new Error(data?.createInquiryChat.message || 'The message was not sent.');
      onClose();
      // `stay` tells the chat page to read the chat document, not a car booking.
      navigate(`/chat/${chatId}`, { state: { stay: true } });
    } catch (e) {
      setError(stayErrorMessage(e, 'The message was not sent.'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Contact worker" modalWidth="480px">
      <Stack $gap={6} data-testid="contact-worker-modal">
        <Stack $gap={4}>
          <Strong style={{ fontSize: 20 }}>Message {workerName}</Strong>
          <Muted $size="sm">
            Ask about the job, supplies or timing. You are not charged. If you book, the
            conversation continues in your job.
          </Muted>
        </Stack>
        <Field label="Your question" htmlFor="contact-worker-message" required>
          <Textarea
            id="contact-worker-message"
            rows={5}
            maxLength={1000}
            value={message}
            onChange={e => setMessage(e.target.value)}
            data-testid="contact-worker-message"
          />
        </Field>
        {error && (
          <Alert tone="error" title="Not sent">
            {error}
          </Alert>
        )}
        <Button
          text="Send message"
          fullWidth
          isDisabled={!message.trim()}
          isLoading={loading}
          onClick={send}
          dataTestId="contact-worker-send"
        />
      </Stack>
    </Modal>
  );
}
